import React, { useState } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const STATUS_TONE = {
  proposed: 'bg-amber-100 text-amber-800 border-amber-300',
  executed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-slate-100 text-slate-700 border-slate-300',
  failed: 'bg-rose-100 text-rose-800 border-rose-300',
};

const fmtParams = (params) => Object.entries(params || {})
  .filter(([, v]) => v !== null && v !== undefined && v !== '')
  .slice(0, 8);

const errMsg = (e) => {
  if (!e) return 'Action failed. Morgan could not reach the server, so nothing was saved.';
  if (typeof e === 'string') return e;
  if (e.message) return e.message;
  const code = e.code ? e.code.replace(/_/g, ' ') : 'unknown error';
  return `Action failed: ${code}${e.field ? ` (${e.field})` : ''}.`;
};

export const MorganActionCard = ({ action, canExecute, onConfirm, onReject }) => {
  const [busy, setBusy] = useState(null);
  const run = async (kind, fn) => { setBusy(kind); try { await fn(action); } finally { setBusy(null); } };
  const out = action.output || {};
  return (
    <div className="rounded-lg border bg-white p-3 space-y-2 text-sm" data-testid={`morgan-action-card-${action.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{action.summary}</span>
        <Badge className={`border font-normal ${STATUS_TONE[action.status] || ''}`} data-testid={`morgan-action-status-${action.id}`}>{action.status}</Badge>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-slate-600">
        {fmtParams(action.params).map(([k, v]) => (
          <React.Fragment key={k}><dt className="font-mono">{k}</dt><dd className="truncate">{String(typeof v === 'object' ? JSON.stringify(v) : v)}</dd></React.Fragment>
        ))}
      </dl>
      {action.status === 'proposed' && (
        <div className="flex gap-2 pt-1">
          <Button size="sm" disabled={!canExecute || !!busy} onClick={() => run('ok', onConfirm)} data-testid={`morgan-action-confirm-${action.id}`}>
            {busy === 'ok' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Confirm
          </Button>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run('no', onReject)} data-testid={`morgan-action-reject-${action.id}`}>
            <X className="w-3.5 h-3.5 mr-1" /> Reject
          </Button>
        </div>
      )}
      {action.status === 'executed' && out.case_type === 'import_reexport' && (
        <div className="text-xs space-y-0.5" data-testid={`morgan-reexport-result-${action.id}`}>
          <div>{out.created} case{out.created === 1 ? '' : 's'} saved to Linkage Pending{out.already_existed ? `, ${out.already_existed} already existed` : ''}.</div>
          {out.skipped_needs_review_or_blocked > 0 && <div className="text-amber-700">{out.skipped_needs_review_or_blocked} row(s) left out: below the auto-match threshold or blocked.</div>}
          <a href={out.path} className="underline text-sky-700" data-testid={`morgan-reexport-open-${action.id}`}>Open Linkage Pending cases</a>
        </div>
      )}
      {action.status === 'executed' && out.path && out.case_type !== 'import_reexport' && (
        <a href={out.path} className="text-xs underline text-sky-700" data-testid={`morgan-action-open-${action.id}`}>Open {out.case_type} case</a>
      )}
      {action.status === 'executed' && out.counts && (
        <div className="text-xs" data-testid={`morgan-match-counts-${action.id}`}>
          Auto {out.counts.auto} · Review {out.counts.review} · Blocked {out.counts.block} · No MRN {out.counts.no_mrn}{out.counts.override ? ` · Override ${out.counts.override}` : ''}
        </div>
      )}
      {action.status === 'failed' && <div className="text-xs text-rose-700" data-testid={`morgan-action-error-${action.id}`}>{errMsg(action.error)}</div>}
    </div>
  );
};
